
import { PropType } from "vue";
import mixins from "@/@core/mixins";
import MixinsUtilities from "@/@shared/propass/services/mixins/MixinsUtilities";
import { FilterConfig, FilterResult } from "./types";

export default mixins(MixinsUtilities).extend({
  props: {
    filters: {
      type: Array as PropType<FilterConfig[]>,
      required: true,
    },
    value: {
      type: Array as PropType<FilterResult[]>,
      required: true,
    },
    // optionnal props
    forceResetFiltersOnLoad: {
      type: Boolean,
      required: false,
      default: false,
    },
    hasMarginRightOnIcon: {
      type: Boolean,
      required: false,
      default: false,
    },
    hasSeparator: {
      type: Boolean,
      required: false,
      default: true,
    },
    hideCollapseBtn: {
      type: Boolean,
      required: false,
      default: false,
    },
    isExpanded: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  watch: {
    filterDatas: {
      deep: true,
      handler() {
        // due to v-form validate() in refresh, nextTick is mandatory for dateRange filters
        // otherwise, onDatePickerInput && when deleted via icon click (X), dateRange value keep its old value :-(
        this.$nextTick(() => {
          this.refresh();
        });
      },
    },
    filters: {
      handler() {
        this.initFilters();
      },
    },
  },
  data() {
    return {
      filterDatas: {} as { [key: string]: { keyOr?: string; operator: string; value: any } },
      initialQuery: [] as FilterResult[],
      isFilterExpanded: [] as boolean[],
      valid: false,
    };
  },
  mounted() {
    this.initFilters();
  },
  methods: {
    initFilters() {
      for (const filter of this.filters) {
        this.resetFilter(filter);
      }
      this.initialQuery = (this.value as FilterResult[]).filter(
        filterResult => !(this.filters as FilterConfig[]).find(filterConfig => filterConfig.key === filterResult.key)
      );
    },
    resetFilter(filter: FilterConfig) {
      const existingFilterResult = this.value
        ? this.forceResetFiltersOnLoad
          ? undefined
          : (this.value as FilterResult[]).find(filterResult => filterResult.key === filter.key)
        : undefined;
      // expand filter if existingFilterResult
      this.isFilterExpanded.push(this.isExpanded ? true : !!existingFilterResult);

      if (filter.operator == "BETWEEN") {
        this.$set(this.filterDatas, filter.key, {
          value: existingFilterResult ? existingFilterResult.value : { from: undefined, to: undefined },
          operator: filter.operator,
        });
      } else if (filter.operator == "IN") {
        this.$set(this.filterDatas, filter.key, {
          value: existingFilterResult ? existingFilterResult.value : [],
          operator: filter.operator,
        });
      } else if (filter.operator == "OR=") {
        this.$set(this.filterDatas, filter.key, {
          value: existingFilterResult ? existingFilterResult.value : undefined,
          keyOr: filter.keyOr,
          operator: filter.operator,
        });
      } else {
        this.$set(this.filterDatas, filter.key, {
          value: existingFilterResult ? existingFilterResult.value : undefined,
          operator: filter.operator,
        });
      }
    },
    isFilterActive(key: string, data: any) {
      const allowedFilter = this.filters.find((filter: { key: string }) => filter.key === key);
      if (allowedFilter) {
        if (data.operator == "BETWEEN") {
          return !!data.value.to || !!data.value.from;
        } else if (data.operator == "IN") {
          return true;
        } else if (data.value !== null && data.value !== undefined && data.value !== "") {
          return true;
        }
      }
      return false;
    },
    refresh() {
      (this.$refs.form as Vue & { validate: () => boolean }).validate();
      this.$nextTick(() => {
        if (this.valid) {
          const filter = [];
          for (const [key, data] of Object.entries(this.filterDatas)) {
            if (this.isFilterActive(key, data)) {
              // deep copy data only for dateRange value (only one to use "BETWEEN" operator)
              // to avoid having same newValue/oldValue in watchers (? referenced objects even if deepWtach ?)
              let deepCopyOfValue = null as object | null;
              if (data.operator === "BETWEEN") {
                deepCopyOfValue = {};
                for (const [key, value] of Object.entries(data.value)) {
                  Object.assign(deepCopyOfValue, { [key]: value });
                }
              }
              const filterToPush = {
                key: key,
                operator: data.operator,
                value:
                  data.operator == "IN" && data.value.length == 0
                    ? this.filters.find((f: any) => f.key === key)!.options!.map((opt: any) => opt.value)
                    : deepCopyOfValue ?? data.value,
              };
              if (data.operator === "OR=") {
                Object.assign(filterToPush, {
                  keyOr: data.keyOr,
                });
              }
              filter.push(filterToPush);
            }
          }
          this.$emit("input", filter.concat(this.initialQuery));
        }
      });
    },
  },
});
